/**
 * Sedes físicas de la institución (`/api/campus`).
 *
 * Distintas de `Area`: `Campus` es la ubicación GEOGRÁFICA y `Area` la estructura
 * ACADÉMICA. La relación es M:N — un programa puede impartirse en varias sedes.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { Campus } from '@/interfaces/Campus'
import { http } from '@/services/http'

const BASE = '/api/campus'

/**
 * Consulta campus · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Campus[]
 */
export async function getCampusService(): Promise<Campus[]> {
  const result = await http.get<any>(BASE)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : []
}

/**
 * Consulta campus · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Campus | null
 */
export async function getCampusByIdService(id: number): Promise<Campus | null> {
  const result = await http.get<Campus>(`${BASE}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea campus · petición `POST`.
 *
 * @returns { ok: boolean; data?: Campus; errors?: Record<string, string[]> }
 */
export async function crearCampusService(
  payload: Pick<Campus, 'nombre' | 'estado'>
): Promise<{ ok: boolean; data?: Campus; errors?: Record<string, string[]> }> {
  const result = await http.post<Campus>(BASE, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Actualiza campus · petición `PUT`.
 *
 * @returns { ok: boolean; data?: Campus; errors?: Record<string, string[]> }
 */
export async function updateCampusService(
  id: number,
  payload: Partial<Pick<Campus, 'nombre' | 'estado'>>
): Promise<{ ok: boolean; data?: Campus; errors?: Record<string, string[]> }> {
  const result = await http.put<Campus>(`${BASE}/${id}`, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Elimina campus · petición `DELETE`.
 *
 * @returns { ok: boolean; errors?: Record<string, string[]> }
 */
export async function eliminarCampusService(
  id: number
): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
  const result = await http.delete(`${BASE}/${id}`)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true }
}
